import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';

export const salesApi = createApi({
	reducerPath: 'salesApi',
	baseQuery: axiosBaseQuery,
	tagTypes: [
		'SalesLeads',
		'SalesLeadDetail',
		'UnassignedLeads',
		'ImportBatches',
		'Zones',
		'SalesTeam',
		'Proposals',
		'Mous',
		'MouTemplates',
		'SalesDashboard',
		'Content',
		'Objections',
		'Handoffs',
		'RevivalQueue',
		'LabTypes',
		'Appointments',
		'ContactList',
		'CoordinatorPerformance',
		'SalesConfig',
		'DeletedLeads',
		'SalesNotifications',
		'FollowUps',
		'AssistantConversations',
		'AssistantConversation',
	],
	endpoints: (builder) => ({
		// ── AI assistant ─
		// Multi-session chat. The sidebar list is cached separately from the open
		// thread so sending a message doesn't refetch every thread's messages.
		listAssistantConversations: builder.query<any, void>({
			query: () => ({ url: '/sales/assistant/conversations' }),
			providesTags: ['AssistantConversations'],
		}),
		getAssistantConversation: builder.query<any, string>({
			query: (id) => ({ url: `/sales/assistant/conversations/${id}` }),
			providesTags: ['AssistantConversation'],
		}),
		createAssistantConversation: builder.mutation<any, void>({
			query: () => ({ url: '/sales/assistant/conversations', method: 'POST' }),
			invalidatesTags: ['AssistantConversations'],
		}),
		renameAssistantConversation: builder.mutation<any, { id: string; title: string }>({
			query: ({ id, title }) => ({
				url: `/sales/assistant/conversations/${id}`,
				method: 'PATCH',
				data: { title },
			}),
			invalidatesTags: ['AssistantConversations'],
		}),
		deleteAssistantConversation: builder.mutation<any, string>({
			query: (id) => ({
				url: `/sales/assistant/conversations/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['AssistantConversations'],
		}),
		sendAssistantMessage: builder.mutation<
			any,
			{ message: string; conversationId?: string }
		>({
			query: (body) => ({
				url: '/sales/assistant/chat',
				method: 'POST',
				data: body,
			}),
			// The page appends the reply locally; only the sidebar (title/ordering)
			// needs refreshing.
			invalidatesTags: ['AssistantConversations'],
		}),

		// ── Vocabulary (dropdowns + filter options) ─
		// One call feeds every sales dropdown so the UI never hardcodes a list
		// that can drift from the backend.
		getSalesConfig: builder.query<any, void>({
			query: () => ({ url: '/sales/config' }),
			providesTags: ['SalesConfig'],
		}),
		getGeoOptions: builder.query<any, Record<string, string> | void>({
			query: (params) => ({ url: '/sales/geo', params: params || undefined }),
		}),
		getLeadFilterOptions: builder.query<any, void>({
			query: () => ({ url: '/sales/leads/filter-options' }),
			providesTags: ['SalesConfig'],
		}),

		// ── Notifications + follow-ups ─
		listSalesNotifications: builder.query<any, void>({
			query: () => ({ url: '/sales/notifications' }),
			providesTags: ['SalesNotifications'],
		}),
		markSalesNotificationRead: builder.mutation<any, string>({
			query: (id) => ({
				url: `/sales/notifications/${id}/read`,
				method: 'PATCH',
			}),
			invalidatesTags: ['SalesNotifications'],
		}),
		markAllSalesNotificationsRead: builder.mutation<any, void>({
			query: () => ({ url: '/sales/notifications/read-all', method: 'PATCH' }),
			invalidatesTags: ['SalesNotifications'],
		}),
		getFollowUpSummary: builder.query<any, void>({
			query: () => ({ url: '/sales/follow-ups' }),
			providesTags: ['FollowUps'],
		}),
		createLeadSource: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: '/sales/sources', method: 'POST', data: body }),
			invalidatesTags: ['SalesConfig'],
		}),
		updateLeadSource: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/sources/${id}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['SalesConfig'],
		}),
		deleteLeadSource: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/sources/${id}`, method: 'DELETE' }),
			invalidatesTags: ['SalesConfig'],
		}),
		suggestLeadTags: builder.query<any, string>({
			query: (name) => ({ url: '/sales/tags/suggest', params: { name } }),
		}),
		createLeadTag: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: '/sales/tags', method: 'POST', data: body }),
			invalidatesTags: ['SalesConfig'],
		}),
		updateLeadTag: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/tags/${id}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['SalesConfig'],
		}),
		deleteLeadTag: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/tags/${id}`, method: 'DELETE' }),
			invalidatesTags: ['SalesConfig'],
		}),

		listCoordinators: builder.query<any, void>({
			query: () => ({ url: '/sales/coordinators' }),
			providesTags: ['SalesTeam'],
		}),

		// Records a call outcome; the backend derives the lead's status and its
		// attempt counter from the disposition.
		logDisposition: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/contact-list/${id}/disposition`,
				method: 'POST',
				data,
			}),
			invalidatesTags: ['ContactList', 'SalesLeads', 'SalesDashboard'],
		}),

		// ── Soft delete (Admin) ─
		listDeletedLeads: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/sales/leads/deleted',
				params: params || undefined,
			}),
			providesTags: ['DeletedLeads'],
		}),
		deleteLead: builder.mutation<any, { id: string; reason?: string }>({
			query: ({ id, reason }) => ({
				url: `/sales/leads/${id}`,
				method: 'DELETE',
				data: { reason },
			}),
			invalidatesTags: [
				'SalesLeads',
				'DeletedLeads',
				'UnassignedLeads',
				'ContactList',
				'SalesDashboard',
			],
		}),
		bulkDeleteLeads: builder.mutation<any, { leadIds: string[]; reason?: string }>({
			query: (body) => ({
				url: '/sales/leads/bulk-delete',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: [
				'SalesLeads',
				'DeletedLeads',
				'UnassignedLeads',
				'ContactList',
				'SalesDashboard',
			],
		}),
		restoreLead: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/leads/${id}/restore`, method: 'POST' }),
			invalidatesTags: [
				'SalesLeads',
				'DeletedLeads',
				'UnassignedLeads',
				'ContactList',
				'SalesDashboard',
			],
		}),

		// ── Dashboards ─
		getSalespersonDashboard: builder.query<any, void>({
			query: () => ({ url: '/sales/dashboard/salesperson' }),
			providesTags: ['SalesDashboard'],
		}),
		getManagerDashboard: builder.query<any, void>({
			query: () => ({ url: '/sales/dashboard/manager' }),
			providesTags: ['SalesDashboard'],
		}),

		// ── Team ─
		listSalespersons: builder.query<any, void>({
			query: () => ({ url: '/sales/team/salespersons' }),
			providesTags: ['SalesTeam'],
		}),
		listSalesManagers: builder.query<any, void>({
			query: () => ({ url: '/sales/team/managers' }),
			providesTags: ['SalesTeam'],
		}),
		createSalesperson: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/sales/team/salespersons',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SalesTeam'],
		}),
		updateSalesperson: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/team/salespersons/${id}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['SalesTeam'],
		}),
		createSalesManager: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/sales/team/managers',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['SalesTeam'],
		}),

		// ── Zones ─
		listZones: builder.query<any, void>({
			query: () => ({ url: '/sales/zones' }),
			providesTags: ['Zones'],
		}),
		createZone: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: '/sales/zones', method: 'POST', data: body }),
			invalidatesTags: ['Zones', 'UnassignedLeads'],
		}),
		updateZone: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/zones/${id}`,
				method: 'PUT',
				data,
			}),
			invalidatesTags: ['Zones', 'UnassignedLeads'],
		}),
		deleteZone: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/zones/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Zones'],
		}),

		// ── Import ─
		importLeads: builder.mutation<any, FormData>({
			query: (formData) => ({
				url: '/sales/leads/import',
				method: 'POST',
				data: formData,
				headers: { 'Content-Type': 'multipart/form-data' },
			}),
			invalidatesTags: ['ImportBatches'],
		}),
		confirmImport: builder.mutation<
			any,
			{
				batchId: string;
				includeDuplicates?: boolean;
				/** Raw sheet value → existing slug or '__create__' (see MappingStep). */
				sourceMap?: Record<string, string>;
				tagMap?: Record<string, string>;
			}
		>({
			query: ({ batchId, includeDuplicates, sourceMap, tagMap }) => ({
				url: `/sales/leads/import/${batchId}/confirm`,
				method: 'POST',
				data: { includeDuplicates, sourceMap, tagMap },
			}),
			invalidatesTags: [
				'ImportBatches',
				'UnassignedLeads',
				'SalesLeads',
				'SalesDashboard',
				// Confirming can create sources/tags via the mapping step.
				'SalesConfig',
			],
		}),
		discardImport: builder.mutation<any, string>({
			query: (batchId) => ({
				url: `/sales/leads/import/${batchId}/discard`,
				method: 'POST',
			}),
			invalidatesTags: ['ImportBatches'],
		}),
		listImportBatches: builder.query<any, void>({
			query: () => ({ url: '/sales/leads/import/batches' }),
			providesTags: ['ImportBatches'],
		}),
		getImportBatch: builder.query<any, string>({
			query: (batchId) => ({
				url: `/sales/leads/import/batches/${batchId}`,
			}),
			providesTags: ['ImportBatches'],
		}),

		// ── Distribution ─
		listUnassignedLeads: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/sales/leads/unassigned',
				params: params || undefined,
			}),
			providesTags: ['UnassignedLeads'],
		}),
		assignLeads: builder.mutation<
			any,
			{
				leadIds: string[];
				salespersonId?: string;
				managerId?: string;
				coordinatorId?: string;
				/** Required when displacing an existing owner. */
				reason?: string;
			}
		>({
			query: (body) => ({
				url: '/sales/leads/assign',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, arg) => [
				'UnassignedLeads',
				'SalesLeads',
				'SalesTeam',
				'SalesDashboard',
				// Routing to a coordinator changes their calling desk.
				'ContactList',
				// Refresh the transfer chain on any lead that just moved.
				...arg.leadIds.map((id) => ({ type: 'SalesLeadDetail' as const, id })),
			],
		}),

		// ── Pipeline ─
		listLeads: builder.query<any, Record<string, string> | void>({
			query: (params) => ({ url: '/sales/leads', params: params || undefined }),
			providesTags: ['SalesLeads'],
		}),
		createLead: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: '/sales/leads', method: 'POST', data: body }),
			// A new lead can land in the pipeline, the coordinator's contact list, or
			// the unassigned pool — refresh them all so it appears without a reload.
			invalidatesTags: [
				'SalesLeads',
				'SalesDashboard',
				'ContactList',
				'UnassignedLeads',
			],
		}),
		getLeadDetail: builder.query<any, string>({
			query: (id) => ({ url: `/sales/leads/${id}` }),
			providesTags: (_r, _e, id) => [{ type: 'SalesLeadDetail', id }],
		}),
		updateLead: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/leads/${id}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'SalesLeadDetail', id },
				'SalesLeads',
			],
		}),
		updateLeadStage: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/leads/${id}/stage`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'SalesLeadDetail', id },
				'SalesLeads',
				'SalesDashboard',
			],
		}),
		logVisit: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/leads/${id}/visits`,
				method: 'POST',
				data,
			}),
			// A visit auto-completes any appointment it fulfils, so refresh that list.
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'SalesLeadDetail', id },
				'SalesDashboard',
				'Appointments',
			],
		}),
		logInteraction: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/leads/${id}/interactions`,
				method: 'POST',
				data,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'SalesLeadDetail', id },
				'SalesLeads',
			],
		}),

		// ── Coordinator contact list ─
		listContactLeads: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/sales/contact-list',
				params: params || undefined,
			}),
			providesTags: ['ContactList'],
		}),
		setContactStatus: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/contact-list/${id}/status`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['ContactList', 'RevivalQueue'],
		}),
		updateContactLead: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/contact-list/${id}`,
				method: 'PATCH',
				data,
			}),
			// Also refresh the lead detail + pipeline so an edit made from the contact
			// list shows up everywhere that lead is displayed, without a manual reload.
			invalidatesTags: (_r, _e, { id }) => [
				'ContactList',
				'SalesLeads',
				{ type: 'SalesLeadDetail', id },
			],
		}),
		coordinatorPerformance: builder.query<any, void>({
			query: () => ({ url: '/sales/coordinator-performance' }),
			providesTags: ['CoordinatorPerformance'],
		}),

		// ── Appointments ─
		listAppointments: builder.query<any, Record<string, string> | void>({
			query: (params) => ({ url: '/sales/appointments', params: params || {} }),
			providesTags: ['Appointments'],
		}),
		appointmentsPendingCount: builder.query<any, void>({
			query: () => ({ url: '/sales/appointments/pending-count' }),
			providesTags: ['Appointments'],
		}),
		createAppointment: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/sales/appointments',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['Appointments', 'ContactList'],
		}),
		assignAppointment: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/appointments/${id}/assign`,
				method: 'POST',
				data,
			}),
			// Assigning to a salesperson also hands them the lead, so refresh the
			// pipeline/unassigned/dashboard views alongside the appointments list.
			invalidatesTags: [
				'Appointments',
				'SalesLeads',
				'UnassignedLeads',
				'SalesDashboard',
			],
		}),
		forwardAppointment: builder.mutation<
			any,
			{ id: string; managerId: string }
		>({
			query: ({ id, managerId }) => ({
				url: `/sales/appointments/${id}/forward`,
				method: 'POST',
				data: { managerId },
			}),
			// Forwarding hands the lead to the chosen manager, so refresh the
			// pipeline/unassigned/dashboard views alongside the appointments list.
			invalidatesTags: [
				'Appointments',
				'SalesLeads',
				'UnassignedLeads',
				'SalesDashboard',
			],
		}),
		completeAppointment: builder.mutation<
			any,
			{ id: string; data?: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/appointments/${id}/complete`,
				method: 'POST',
				data: data || {},
			}),
			invalidatesTags: ['Appointments'],
		}),

		// ── Proposals ─
		getLabCatalog: builder.query<any, void>({
			query: () => ({ url: '/sales/catalog' }),
			providesTags: ['LabTypes'],
		}),
		listLabTypes: builder.query<any, void>({
			query: () => ({ url: '/sales/lab-types' }),
			providesTags: ['LabTypes'],
		}),
		createLabType: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/sales/lab-types',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['LabTypes'],
		}),
		updateLabType: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/lab-types/${id}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['LabTypes'],
		}),
		deleteLabType: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/lab-types/${id}`, method: 'DELETE' }),
			invalidatesTags: ['LabTypes'],
		}),
		listProposals: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/sales/proposals',
				params: params || undefined,
			}),
			providesTags: ['Proposals'],
		}),
		getProposalView: builder.query<any, string>({
			query: (id) => ({ url: `/sales/proposals/${id}/view` }),
			providesTags: (_r, _e, id) => [{ type: 'Proposals', id }],
		}),
		createProposal: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/sales/proposals',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['Proposals', 'SalesLeadDetail'],
		}),
		submitProposal: builder.mutation<any, string>({
			query: (id) => ({
				url: `/sales/proposals/${id}/submit`,
				method: 'PATCH',
			}),
			invalidatesTags: ['Proposals', 'SalesLeadDetail', 'SalesDashboard'],
		}),
		approveProposal: builder.mutation<
			any,
			{
				id: string;
				edits?: {
					index: number;
					labDiscountPercent: number;
					kitDiscountPercent: number;
				}[];
			}
		>({
			query: ({ id, edits }) => ({
				url: `/sales/proposals/${id}/approve`,
				method: 'PATCH',
				data: edits && edits.length ? { edits } : {},
			}),
			// Approval sends the proposal too, so refresh the lead detail + leads.
			invalidatesTags: [
				'Proposals',
				'SalesDashboard',
				'SalesLeadDetail',
				'SalesLeads',
			],
		}),
		rejectProposal: builder.mutation<any, { id: string; reason?: string }>({
			query: ({ id, reason }) => ({
				url: `/sales/proposals/${id}/reject`,
				method: 'PATCH',
				data: { reason },
			}),
			invalidatesTags: ['Proposals', 'SalesDashboard'],
		}),
		sendProposal: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/proposals/${id}/send`, method: 'POST' }),
			invalidatesTags: [
				'Proposals',
				'SalesLeadDetail',
				'SalesLeads',
				'SalesDashboard',
			],
		}),

		// ── MOU ─
		listMouTemplates: builder.query<any, void>({
			query: () => ({ url: '/sales/mou/templates' }),
			providesTags: ['MouTemplates'],
		}),
		createMouTemplate: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/sales/mou/templates',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['MouTemplates'],
		}),
		updateMouTemplate: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/mou/templates/${id}`,
				method: 'PUT',
				data,
			}),
			invalidatesTags: ['MouTemplates'],
		}),
		listMous: builder.query<any, Record<string, string> | void>({
			query: (params) => ({ url: '/sales/mou', params: params || undefined }),
			providesTags: ['Mous'],
		}),
		getMou: builder.query<any, string>({
			query: (id) => ({ url: `/sales/mou/${id}` }),
			providesTags: (_r, _e, id) => [{ type: 'Mous', id }],
		}),
		createMou: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: '/sales/mou', method: 'POST', data: body }),
			invalidatesTags: ['Mous', 'SalesLeadDetail'],
		}),
		submitMou: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/mou/${id}/submit`, method: 'PATCH' }),
			invalidatesTags: ['Mous', 'SalesLeadDetail', 'SalesDashboard'],
		}),
		approveMou: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/mou/${id}/approve`, method: 'PATCH' }),
			invalidatesTags: ['Mous', 'SalesDashboard'],
		}),
		rejectMou: builder.mutation<any, { id: string; reason?: string }>({
			query: ({ id, reason }) => ({
				url: `/sales/mou/${id}/reject`,
				method: 'PATCH',
				data: { reason },
			}),
			invalidatesTags: ['Mous', 'SalesDashboard'],
		}),
		sendMou: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/mou/${id}/send`, method: 'POST' }),
			invalidatesTags: ['Mous', 'SalesLeadDetail', 'SalesDashboard'],
		}),
		uploadPhysicalMou: builder.mutation<
			any,
			{ id: string; url: string; signatoryName?: string }
		>({
			query: ({ id, url, signatoryName }) => ({
				url: `/sales/mou/${id}/physical-upload`,
				method: 'POST',
				data: { url, signatoryName },
			}),
			invalidatesTags: ['Mous', 'SalesLeadDetail', 'SalesDashboard'],
		}),

		// ── Public (no auth) ─
		publicViewMou: builder.query<any, string>({
			query: (token) => ({ url: `/sales-public/mou/${token}` }),
		}),
		publicRequestMouOtp: builder.mutation<
			any,
			{ token: string; phone: string; name?: string }
		>({
			query: ({ token, phone, name }) => ({
				url: `/sales-public/mou/${token}/request-otp`,
				method: 'POST',
				data: { phone, name },
			}),
		}),
		publicRequestMouEmailOtp: builder.mutation<
			any,
			{ token: string; name: string; email: string }
		>({
			query: ({ token, name, email }) => ({
				url: `/sales-public/mou/${token}/request-email-otp`,
				method: 'POST',
				data: { name, email },
			}),
		}),
		publicSignMou: builder.mutation<
			any,
			{ token: string; data: Record<string, unknown> }
		>({
			query: ({ token, data }) => ({
				url: `/sales-public/mou/${token}/sign`,
				method: 'POST',
				data,
			}),
		}),
		publicViewProposal: builder.query<any, string>({
			query: (token) => ({ url: `/sales-public/proposal/${token}` }),
		}),

		// ── Content library ─
		listContent: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/sales/content',
				params: params || undefined,
			}),
			providesTags: ['Content'],
		}),
		createContent: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({ url: '/sales/content', method: 'POST', data: body }),
			invalidatesTags: ['Content'],
		}),
		updateContent: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/content/${id}`,
				method: 'PUT',
				data,
			}),
			invalidatesTags: ['Content'],
		}),
		deleteContent: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/content/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Content'],
		}),
		logContentUsage: builder.mutation<
			any,
			{ id: string; leadId?: string }
		>({
			query: ({ id, leadId }) => ({
				url: `/sales/content/${id}/use`,
				method: 'POST',
				data: { leadId },
			}),
			// Deliberately does NOT invalidate 'Content': logging a view while the
			// presenter is open would refetch + re-sort the list, shifting the item
			// under the current index. The usage count refreshes on the next load.
		}),

		// ── Objection playbook ─
		listObjections: builder.query<any, void>({
			query: () => ({ url: '/sales/objections' }),
			providesTags: ['Objections'],
		}),
		createObjection: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/sales/objections',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['Objections'],
		}),
		updateObjection: builder.mutation<
			any,
			{ id: string; data: Record<string, unknown> }
		>({
			query: ({ id, data }) => ({
				url: `/sales/objections/${id}`,
				method: 'PUT',
				data,
			}),
			invalidatesTags: ['Objections'],
		}),
		deleteObjection: builder.mutation<any, string>({
			query: (id) => ({ url: `/sales/objections/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Objections'],
		}),

		// ── Revival + upsell ─
		listRevivalQueue: builder.query<any, void>({
			query: () => ({ url: '/sales/revival-queue' }),
			providesTags: ['RevivalQueue'],
		}),
		listUpsell: builder.query<any, void>({
			query: () => ({ url: '/sales/upsell' }),
		}),

		// ── Handoff ─
		getSalesActivity: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/sales/activity',
				params: params || undefined,
			}),
		}),
		listHandoffs: builder.query<any, Record<string, string> | void>({
			query: (params) => ({
				url: '/sales/handoffs',
				params: params || undefined,
			}),
			providesTags: ['Handoffs'],
		}),
		getHandoff: builder.query<any, string>({
			query: (id) => ({ url: `/sales/handoffs/${id}` }),
			providesTags: (_r, _e, id) => [{ type: 'Handoffs', id }],
		}),
		getHandoffTrainers: builder.query<any, string>({
			query: (id) => ({ url: `/sales/handoffs/${id}/trainers` }),
		}),
		toggleChecklistItem: builder.mutation<
			any,
			{ id: string; key: string; done: boolean }
		>({
			query: ({ id, key, done }) => ({
				url: `/sales/handoffs/${id}/checklist`,
				method: 'PATCH',
				data: { key, done },
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Handoffs', id },
				'Handoffs',
			],
		}),
		assignHandoffTrainer: builder.mutation<
			any,
			{ id: string; trainerId: string }
		>({
			query: ({ id, trainerId }) => ({
				url: `/sales/handoffs/${id}/trainer`,
				method: 'PATCH',
				data: { trainerId },
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Handoffs', id },
				'Handoffs',
			],
		}),
	}),
});

export const {
	useListAssistantConversationsQuery,
	useGetAssistantConversationQuery,
	useCreateAssistantConversationMutation,
	useRenameAssistantConversationMutation,
	useDeleteAssistantConversationMutation,
	useSendAssistantMessageMutation,
	useGetSalesConfigQuery,
	useGetGeoOptionsQuery,
	useGetLeadFilterOptionsQuery,
	useListSalesNotificationsQuery,
	useMarkSalesNotificationReadMutation,
	useMarkAllSalesNotificationsReadMutation,
	useGetFollowUpSummaryQuery,
	useCreateLeadSourceMutation,
	useUpdateLeadSourceMutation,
	useDeleteLeadSourceMutation,
	useSuggestLeadTagsQuery,
	useCreateLeadTagMutation,
	useUpdateLeadTagMutation,
	useDeleteLeadTagMutation,
	useListCoordinatorsQuery,
	useLogDispositionMutation,
	useListDeletedLeadsQuery,
	useDeleteLeadMutation,
	useBulkDeleteLeadsMutation,
	useRestoreLeadMutation,
	useGetSalespersonDashboardQuery,
	useGetManagerDashboardQuery,
	useListSalespersonsQuery,
	useListSalesManagersQuery,
	useCreateSalespersonMutation,
	useUpdateSalespersonMutation,
	useCreateSalesManagerMutation,
	useListZonesQuery,
	useCreateZoneMutation,
	useUpdateZoneMutation,
	useDeleteZoneMutation,
	useImportLeadsMutation,
	useConfirmImportMutation,
	useDiscardImportMutation,
	useListImportBatchesQuery,
	useGetImportBatchQuery,
	useListUnassignedLeadsQuery,
	useAssignLeadsMutation,
	useListLeadsQuery,
	useCreateLeadMutation,
	useGetLeadDetailQuery,
	useUpdateLeadMutation,
	useUpdateLeadStageMutation,
	useLogVisitMutation,
	useLogInteractionMutation,
	useListContactLeadsQuery,
	useSetContactStatusMutation,
	useUpdateContactLeadMutation,
	useCoordinatorPerformanceQuery,
	useListAppointmentsQuery,
	useAppointmentsPendingCountQuery,
	useCreateAppointmentMutation,
	useAssignAppointmentMutation,
	useForwardAppointmentMutation,
	useCompleteAppointmentMutation,
	useGetLabCatalogQuery,
	useListLabTypesQuery,
	useCreateLabTypeMutation,
	useUpdateLabTypeMutation,
	useDeleteLabTypeMutation,
	useListProposalsQuery,
	useGetProposalViewQuery,
	useCreateProposalMutation,
	useSubmitProposalMutation,
	useApproveProposalMutation,
	useRejectProposalMutation,
	useSendProposalMutation,
	useListMouTemplatesQuery,
	useCreateMouTemplateMutation,
	useUpdateMouTemplateMutation,
	useListMousQuery,
	useGetMouQuery,
	useCreateMouMutation,
	useSubmitMouMutation,
	useApproveMouMutation,
	useRejectMouMutation,
	useSendMouMutation,
	useUploadPhysicalMouMutation,
	usePublicViewMouQuery,
	usePublicRequestMouOtpMutation,
	usePublicRequestMouEmailOtpMutation,
	usePublicSignMouMutation,
	usePublicViewProposalQuery,
	useListContentQuery,
	useCreateContentMutation,
	useUpdateContentMutation,
	useDeleteContentMutation,
	useLogContentUsageMutation,
	useListObjectionsQuery,
	useCreateObjectionMutation,
	useUpdateObjectionMutation,
	useDeleteObjectionMutation,
	useListRevivalQueueQuery,
	useListUpsellQuery,
	useGetSalesActivityQuery,
	useListHandoffsQuery,
	useGetHandoffQuery,
	useGetHandoffTrainersQuery,
	useToggleChecklistItemMutation,
	useAssignHandoffTrainerMutation,
} = salesApi;
